import React, { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Sparkles, Send, User } from "lucide-react";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "Explain the PCT route for filing a US patent internationally",
  "What's the difference between a design patent and a utility patent?",
  "How do I file a trademark via the Madrid Protocol?",
  "Give me a checklist for valuing a SaaS patent portfolio",
];

export default function Copilot() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim()) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/chat", {
        session_id: sessionId,
        message: msg,
      });
      setSessionId(data.session_id);
      setMessages(data.messages);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col" data-testid="copilot-page">
      <div className="p-6 md:p-8 border-b border-slate-200">
        <div className="eyebrow">AI Assistant</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mt-1">IP Copilot</h1>
        <p className="text-slate-600 mt-1 text-sm">Powered by Claude Sonnet 4.5 — ask anything about global IP, valuation, or filing strategy.</p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#F1F5F9]">
        <div className="max-w-3xl mx-auto p-6 space-y-5">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-[#B33A3A] mb-4">
                <Sparkles size={24} className="text-white" />
              </div>
              <h2 className="font-serif text-2xl font-medium mb-2">How can I help with your IP?</h2>
              <p className="text-slate-600 text-sm max-w-md mx-auto mb-8">
                Ask about treaties, filing strategies, valuation approaches, or draft responses to office actions.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    data-testid={`quick-prompt-${QUICK_PROMPTS.indexOf(p)}`}
                    className="bengal-card p-4 text-left text-sm hover:border-[#B33A3A] hover:shadow-sm transition-all"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-sm bg-[#B33A3A] text-white flex items-center justify-center shrink-0">
                  <Sparkles size={14} />
                </div>
              )}
              <div className={`max-w-[75%] px-4 py-3 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#B33A3A] text-white"
                  : "bg-white border border-slate-200 text-slate-800"
              }`}>
                {m.content}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-sm bg-slate-700 text-white flex items-center justify-center shrink-0">
                  <User size={14} />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-[#B33A3A] text-white flex items-center justify-center shrink-0">
                <Sparkles size={14} />
              </div>
              <div className="px-4 py-3 bg-white border border-slate-200 rounded-sm text-sm text-slate-500">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "100ms" }} />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "200ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-slate-200 bg-white p-4"
        data-testid="copilot-form"
      >
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about patents, trademarks, licensing, or valuation…"
            data-testid="copilot-input"
            disabled={loading}
            className="flex-1 border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#B33A3A] focus:border-transparent"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="copilot-send-btn"
            className="bengal-btn-primary flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            <Send size={14} /> Send
          </button>
        </div>
      </form>
    </div>
  );
}
